export interface AudioSettings {
  musicEnabled: boolean;
  sfxEnabled: boolean;
  musicVolume: number;
  sfxVolume: number;
}

const AUDIO_SETTINGS_KEY = 'choreboard_audio_settings';

const DEFAULT_SETTINGS: AudioSettings = {
  musicEnabled: false,
  sfxEnabled: false,
  musicVolume: 0.7,
  sfxVolume: 0.5,
};

let audioUnlocked = false;
let audioCtx: AudioContext | null = null;
const sfxBufferCache = new Map<string, AudioBuffer>();

function getOrCreateAudioContext(): AudioContext {
  if (!audioCtx) {
    audioCtx = new AudioContext();
  }
  return audioCtx;
}

export function clearSfxCache(): void {
  sfxBufferCache.clear();
}

export async function preloadAllSfx(): Promise<void> {
  const ctx = getOrCreateAudioContext();
  await Promise.all(
    Object.values(SoundType).map(async url => {
      if (sfxBufferCache.has(url)) return;
      try {
        const response = await fetch(url);
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer =
          await ctx.decodeAudioData(arrayBuffer);
        sfxBufferCache.set(url, audioBuffer);
      } catch (e) {
        console.warn(`[SFX] Failed to preload ${url}:`, e);
      }
    })
  );
}

export function getAudioSettings(): AudioSettings {
  try {
    const stored = localStorage.getItem(AUDIO_SETTINGS_KEY);
    if (stored) {
      return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
    }
  } catch (e) {
    console.warn('Failed to load audio settings:', e);
  }
  return DEFAULT_SETTINGS;
}

export function saveAudioSettings(
  settings: AudioSettings
): void {
  try {
    localStorage.setItem(
      AUDIO_SETTINGS_KEY,
      JSON.stringify(settings)
    );
  } catch (e) {
    console.warn('Failed to save audio settings:', e);
  }
}

export function unlockAudio(): void {
  if (audioUnlocked) return;

  const audio = new Audio();
  audio.volume = 0;
  audio
    .play()
    .then(() => {
      audio.pause();
      audioUnlocked = true;
    })
    .catch(() => {
      // Silently fail - will retry on next interaction
    });

  const ctx = getOrCreateAudioContext();
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }
}

export enum SoundType {
  // button
  MenuClick = '/audio/sfx/Menu_Click.ogg',
  Confirm = '/audio/sfx/020_Confirm_10.wav',
  Cancel = '/audio/sfx/cancel_button.ogg',
  // navigation
  ChangeTab = '/audio/sfx/Change_Tab_1.wav',
  Navigate = '/audio/sfx/navigate.wav',
  Login = '/audio/sfx/login.ogg',
  // modal
  InfoModal = '/audio/sfx/info_modal.ogg',
  RewardModal = '/audio/sfx/reward_modal.ogg',
  CloseModal = '/audio/sfx/032_Denied_02.wav',
  // inventory
  PickItem = '/audio/sfx/pick_item.wav',
  // quests
  QuestCreate = '/audio/sfx/quest_create.ogg',
  QuestComplete = '/audio/sfx/quest_complete.ogg',
  CompleteTask = '/audio/sfx/complete_task.ogg',
}

export function playSound(
  path: SoundType,
  volume = 1.0
): void {
  const settings = getAudioSettings();
  if (!settings.sfxEnabled) {
    return;
  }

  const buffer = sfxBufferCache.get(path);
  if (buffer) {
    try {
      const ctx = getOrCreateAudioContext();
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      const gain = ctx.createGain();
      gain.gain.value = settings.sfxVolume * volume;
      source.connect(gain);
      gain.connect(ctx.destination);
      source.start(0);
    } catch (e) {
      console.warn(`[SFX] Error playing ${path}:`, e);
    }
    return;
  }

  try {
    const audio = new Audio(path);
    audio.volume = settings.sfxVolume * volume;
    audio.play().catch(err => {
      console.warn(`[SFX] Error playing ${path}:`, err);
    });
  } catch (e) {
    console.warn(`[SFX] Error playing ${path}:`, e);
  }
}

export async function loadTrack(
  url: string
): Promise<HTMLAudioElement> {
  return new Promise((resolve, reject) => {
    const audio = new Audio();
    audio.src = url;

    const onCanPlay = () => {
      audio.removeEventListener(
        'canplaythrough',
        onCanPlay
      );
      audio.removeEventListener('error', onError);
      resolve(audio);
    };

    const onError = (e: ErrorEvent) => {
      audio.removeEventListener(
        'canplaythrough',
        onCanPlay
      );
      audio.removeEventListener('error', onError);
      reject(e);
    };

    audio.addEventListener('canplaythrough', onCanPlay, {
      once: true,
    });
    audio.addEventListener('error', onError, {
      once: true,
    });
    audio.load();
  });
}

export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}
